import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Snackbar,
  IconButton,
  InputAdornment,
  Divider,
  Grid,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Collapse,
  Checkbox,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  ContentCopy,
  Link as LinkIcon,
  CheckCircle,
  OpenInNew,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import { db } from '../config/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { saveEvaluationConfig } from '../services/evaluationConfigService';
import { saveGeneratedLink } from '../services/linkHistoryService';

function LinkGenerator() {
  const [courseName, setCourseName] = useState('');
  const [userId, setUserId] = useState('');
  const [evaluationTitle, setEvaluationTitle] = useState('O que você achou do treinamento?');
  const [isScorm, setIsScorm] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [allOptions, setAllOptions] = useState({ positive: [], improvement: [] });
  const [selectedPositiveOptions, setSelectedPositiveOptions] = useState([]);
  const [selectedImprovementOptions, setSelectedImprovementOptions] = useState([]);
  const [showOptionsConfig, setShowOptionsConfig] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);

  const baseUrl = process.env.REACT_APP_EVALUATION_URL || 'http://localhost:3000';

  // Carrega todas as opções disponíveis
  useEffect(() => {
    const loadAllOptions = async () => {
      try {
        setLoadingOptions(true);
        const querySnapshot = await getDocs(
          query(collection(db, 'avaliacoes_opcoes'), where('active', '==', true))
        );

        const options = { positive: [], improvement: [] };

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.category === 'positive') {
            options.positive.push(data.text);
          } else if (data.category === 'improvement') {
            options.improvement.push(data.text);
          }
        });

        setAllOptions(options);
      } catch (error) {
        console.error('Erro ao carregar opções:', error);
      } finally {
        setLoadingOptions(false);
      }
    };

    loadAllOptions();
  }, []);

  // Inicializa as opções selecionadas com todas as opções disponíveis
  useEffect(() => {
    if (allOptions.positive.length > 0 || allOptions.improvement.length > 0) {
      setSelectedPositiveOptions(allOptions.positive);
      setSelectedImprovementOptions(allOptions.improvement);
    }
  }, [allOptions]);

  const handleGenerateLink = async () => {
    // Validação
    if (!courseName.trim()) {
      setValidationError('O nome do projeto é obrigatório');
      return;
    }

    if (!userId.trim()) {
      setValidationError('O ID do usuário é obrigatório');
      return;
    }

    setValidationError('');

    // Gera um ID único para esta configuração específica de link
    const configId = `${courseName.trim()}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Salva a configuração com o ID único, incluindo courseName e userId
    try {
      await saveEvaluationConfig(configId, {
        courseName: courseName.trim(),
        userId: userId.trim(), // Salva o userId no Firestore
        isScorm: isScorm, // Indica se é SCORM
        evaluationTitle: evaluationTitle.trim(), // Novo campo para o título da avaliação
        positiveOptions: selectedPositiveOptions,
        improvementOptions: selectedImprovementOptions,
        active: true,
        createdAt: new Date().toISOString()
      });
      setConfigSaved(true);
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
    }

    // Gera o link apenas com o configId
    const params = new URLSearchParams({
      configId: configId
    });

    const link = `${baseUrl}?${params.toString()}`;
    setGeneratedLink(link);

    // Salva o link no histórico
    try {
      const result = await saveGeneratedLink(link, courseName.trim(), userId.trim(), null, isScorm);
      if (result.success) {
        // console.log('Link salvo no histórico com sucesso:', result.message);
      } else {
        console.error('Erro ao salvar link no histórico:', result.message);
      }
    } catch (error) {
      console.error('Erro ao salvar link no histórico:', error);
      // Não interrompe o fluxo principal se houver erro ao salvar no histórico
    }
  };

  const togglePositiveOption = (option) => {
    setSelectedPositiveOptions(prev =>
      prev.includes(option)
        ? prev.filter(item => item !== option)
        : [...prev, option]
    );
  };

  const toggleImprovementOption = (option) => {
    setSelectedImprovementOptions(prev =>
      prev.includes(option)
        ? prev.filter(item => item !== option)
        : [...prev, option]
    );
  };

  const selectAllPositive = () => {
    setSelectedPositiveOptions(allOptions.positive);
  };

  const selectAllImprovement = () => {
    setSelectedImprovementOptions(allOptions.improvement);
  };

  const clearAllPositive = () => {
    setSelectedPositiveOptions([]);
  };

  const clearAllImprovement = () => {
    setSelectedImprovementOptions([]);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setShowSuccess(true);
    } catch (error) {
      console.error('Erro ao copiar link:', error);
      alert('Erro ao copiar link. Tente selecionar e copiar manualmente.');
    }
  };

  const handleOpenLink = () => {
    window.open(generatedLink, '_blank');
  };

  const handleClear = () => {
    setCourseName('');
    setUserId('');
    setEvaluationTitle('O que você achou do treinamento?');
    setIsScorm(false);
    setGeneratedLink('');
    setValidationError('');
    // Reinicia as opções para todas as opções disponíveis
    setSelectedPositiveOptions(allOptions.positive);
    setSelectedImprovementOptions(allOptions.improvement);
  };

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Gerador de Links Personalizados
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Crie links de avaliação personalizados que preenchem automaticamente o projeto e usuário
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <LinkIcon sx={{ mr: 1 }} />
                  Informações do Link
                </Typography>

                <Box sx={{ mt: 3 }}>
                  <TextField
                    fullWidth
                    label="Nome do projeto"
                    variant="outlined"
                    value={courseName}
                    onChange={(e) => setCourseName(e.target.value)}
                    placeholder="Ex: Treinamento de Segurança"
                    helperText="Este será o nome do projeto na avaliação"
                    sx={{ mb: 2, borderRadius: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="ID do Usuário"
                    variant="outlined"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    placeholder="Ex: joao.silva@empresa.com ou ID123"
                    helperText="Identificador único do participante"
                    sx={{ mb: 2, borderRadius: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Título da Avaliação"
                    variant="outlined"
                    value={evaluationTitle}
                    onChange={(e) => setEvaluationTitle(e.target.value)}
                    placeholder="Ex: O que você achou do treinamento?"
                    helperText="Este será o título principal exibido na página de avaliação."
                    sx={{ mb: 2, borderRadius: 2 }}
                  />

                  <Box sx={{
                    mb: 2,
                    p: 2,
                    border: '1px solid',
                    borderColor: isScorm ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    backgroundColor: isScorm ? 'primary.lighter' : 'transparent'
                  }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isScorm}
                          onChange={(e) => setIsScorm(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            Pacote SCORM
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Marque esta opção se a avaliação será inserida dentro de um pacote SCORM
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>

                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => setShowOptionsConfig(!showOptionsConfig)}
                    startIcon={showOptionsConfig ? <ExpandLess /> : <ExpandMore />}
                    sx={{ mb: 2, borderRadius: 2 }}
                  >
                    {showOptionsConfig ? 'Ocultar' : 'Mostrar'} Configuração de Opções
                  </Button>

                  <Collapse in={showOptionsConfig}>
                    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                        Opções Positivas
                      </Typography>

                      {loadingOptions ? (
                        <Typography variant="body2" color="text.secondary">Carregando opções...</Typography>
                      ) : (
                        <>
                          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <Button size="small" variant="outlined" onClick={selectAllPositive}>
                              Selecionar todas
                            </Button>
                            <Button size="small" variant="outlined" onClick={clearAllPositive}>
                              Limpar
                            </Button>
                          </Box>

                          <List dense>
                            {allOptions.positive.map((option) => (
                              <ListItem key={option} disablePadding>
                                <ListItemButton role={undefined} onClick={() => togglePositiveOption(option)} dense>
                                  <Checkbox
                                    edge="start"
                                    checked={selectedPositiveOptions.includes(option)}
                                    tabIndex={-1}
                                    disableRipple
                                  />
                                  <ListItemText primary={option} />
                                </ListItemButton>
                              </ListItem>
                            ))}
                          </List>
                        </>
                      )}

                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                        Opções de Melhoria
                      </Typography>

                      {loadingOptions ? (
                        <Typography variant="body2" color="text.secondary">Carregando opções...</Typography>
                      ) : (
                        <>
                          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <Button size="small" variant="outlined" onClick={selectAllImprovement}>
                              Selecionar todas
                            </Button>
                            <Button size="small" variant="outlined" onClick={clearAllImprovement}>
                              Limpar
                            </Button>
                          </Box>

                          <List dense>
                            {allOptions.improvement.map((option) => (
                              <ListItem key={option} disablePadding>
                                <ListItemButton role={undefined} onClick={() => toggleImprovementOption(option)} dense>
                                  <Checkbox
                                    edge="start"
                                    checked={selectedImprovementOptions.includes(option)}
                                    tabIndex={-1}
                                    disableRipple
                                  />
                                  <ListItemText primary={option} />
                                </ListItemButton>
                              </ListItem>
                            ))}
                          </List>
                        </>
                      )}

                      {configSaved && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                          Configuração de opções salva com sucesso!
                        </Alert>
                      )}
                    </Box>
                  </Collapse>

                  {validationError && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                      {validationError}
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      onClick={handleGenerateLink}
                      startIcon={<LinkIcon />}
                      sx={{ borderRadius: 2 }}
                    >
                      Gerar Link
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={handleClear}
                      sx={{ borderRadius: 2 }}
                    >
                      Limpar
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              sx={{
                backgroundColor: generatedLink ? 'success.lighter' : 'grey.50',
                borderColor: generatedLink ? 'success.main' : 'grey.300',
                borderRadius: 3
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  {generatedLink ? (
                    <>
                      <CheckCircle sx={{ mr: 1, color: 'success.main' }} />
                      Link Gerado
                    </>
                  ) : (
                    <>
                      <LinkIcon sx={{ mr: 1 }} />
                      Resultado
                    </>
                  )}
                </Typography>

                {generatedLink ? (
                  <Box sx={{ mt: 3 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      value={generatedLink}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={handleCopyLink} edge="end">
                              <ContentCopy />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 2,
                        '& .MuiInputBase-root': {
                          backgroundColor: 'white',
                          borderRadius: 2
                        }
                      }}
                    />

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Dados armazenados no Firestore:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={`Projeto: ${courseName}`}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ borderRadius: 1 }}
                        />
                        <Chip
                          label={`Usuário: ${userId}`}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ borderRadius: 1 }}
                        />
                        <Chip
                          label={isScorm ? 'SCORM: Sim' : 'SCORM: Não'}
                          size="small"
                          color={isScorm ? 'primary' : 'default'}
                          variant="outlined"
                          sx={{ borderRadius: 1 }}
                        />
                        <Chip
                          label={`Título: ${evaluationTitle}`}
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ borderRadius: 1 }}
                        />
                        <Chip
                          label={`${selectedPositiveOptions.length + selectedImprovementOptions.length} opções`}
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ borderRadius: 1 }}
                        />
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        startIcon={<ContentCopy />}
                        onClick={handleCopyLink}
                        sx={{ borderRadius: 2 }}
                      >
                        Copiar Link
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<OpenInNew />}
                        onClick={handleOpenLink}
                        sx={{ borderRadius: 2 }}
                      >
                        Testar
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ mt: 3, textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Preencha os campos ao lado e clique em "Gerar Link" para criar um link personalizado
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ backgroundColor: 'info.lighter', p: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Como funciona?
          </Typography>
          <Typography variant="body2" paragraph>
            1. Preencha o nome do projeto e o ID do usuário
          </Typography>
          <Typography variant="body2" paragraph>
            2. Clique em "Gerar Link" para criar o link personalizado
          </Typography>
          <Typography variant="body2" paragraph>
            3. Copie o link e envie para o participante
          </Typography>
          <Typography variant="body2">
            4. Quando o participante acessar o link, os campos de projeto e usuário serão preenchidos automaticamente
          </Typography>
        </Box>
      </Paper>

      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          Link copiado com sucesso!
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default LinkGenerator;